import React, { useState, useEffect } from "react";

// ✅ Reusable TypewriterText
const TypewriterText = ({ text, delay = 80 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, delay, text]);

  return (
    <>
      {displayedText}
      <span className="border-r-2 border-black animate-pulse ml-1"></span>
    </>
  );
};

const FormSection = () => {
  const headingText = "Let’s Connect and Build Stronger Partnerships.";

  // ✅ Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    hearAbout: "Google Search",
    message: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ Form Fields Config (for map)
  const fields = [
    {
      id: "fullName",
      label: "Full name",
      type: "text",
      placeholder: "Enter your Name",
    },
    {
      id: "email",
      label: "Email Address",
      type: "email",
      placeholder: "example@yourmail.com",
    },
    {
      id: "phone",
      label: "Phone number",
      type: "tel",
      placeholder: "Enter your phone number",
    },
  ];

  const options = [
    "Google Search",
    "Social Media",
    "Friends or Family",
    "Online Advertisement",
    "Others",
  ];

  // ✅ Handle Change
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // ✅ Validation
  const validate = () => {
    let newErrors = {};

    if (!formData.fullName.trim())
      newErrors.fullName = "Full name is required.";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address.";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number.";
    }
    if (!formData.message.trim())
      newErrors.message = "Message cannot be empty.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      console.log("Form Submitted ✅", formData);
      alert("Form Submitted Successfully!");
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        hearAbout: "Google Search",
        message: "",
      });
      setErrors({});
    }
  };

  return (
    <section className="py-16 bg-white px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div className="mb-6">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            <span className="text-base sm:text-lg font-medium">
              Contact with Us
            </span>
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-2 leading-snug">
            <TypewriterText text={headingText} delay={70} />
          </h2>
        </div>

        {/* Contact Form */}
        <div className="bg-gray-50 rounded-2xl shadow-sm p-6 sm:p-10">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Mapped Input Fields */}
            {fields.map((field) => (
              <div key={field.id}>
                <label className="block text-gray-700 font-medium mb-2">
                  {field.label}
                </label>
                <input
                  id={field.id}
                  type={field.type}
                  value={formData[field.id]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors[field.id]
                      ? "border-red-500 focus:ring-red-400"
                      : "border-gray-300 focus:ring-purple-200"
                  } focus:ring-2 focus:outline-none`}
                />
                {errors[field.id] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[field.id]}
                  </p>
                )}
              </div>
            ))}

            {/* How did you hear about us */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                How did you hear about us?
              </label>
              <select
                id="hearAbout"
                value={formData.hearAbout}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-200 focus:outline-none"
              >
                {options.map((opt, i) => (
                  <option key={i}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Type your message
              </label>
              <textarea
                id="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project"
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.message
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-purple-200"
                } focus:ring-2 focus:outline-none`}
              ></textarea>
              {errors.message && (
                <p className="text-red-500 text-sm mt-1">{errors.message}</p>
              )}
            </div>

            {/* Submit */}
            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-black text-white font-semibold rounded-full hover:bg-blue-600 transition-all"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FormSection;
